import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, XCircle } from "lucide-react";
import { QUOTE_SENT_CHANNELS, QUOTE_LOST_REASONS } from "@/lib/crm";

// mode: "send" | "outcome"
export default function QuoteActionsDialog({ open, onOpenChange, quote, mode, onSaved }) {
  const [channel, setChannel] = useState("Email");
  const [sentDate, setSentDate] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [outcome, setOutcome] = useState("Accettato");
  const [lostReason, setLostReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(""); setChannel("Email"); setSentDate(""); setFollowUp(""); setOutcome("Accettato"); setLostReason("");
  }, [open]);

  const submit = async () => {
    setError(""); setSaving(true);
    try {
      if (mode === "send") {
        const { data } = await api.post(`/quotes/${quote.id}/send`, {
          channel, sent_date: sentDate || null, follow_up_date: followUp || null,
        });
        toast.success(`Preventivo ${quote.quote_number} segnato come inviato (${channel})`);
        onSaved?.(data);
      } else {
        if (outcome === "Rifiutato" && !lostReason) { setError("Seleziona il motivo del rifiuto"); setSaving(false); return; }
        const { data } = await api.post(`/quotes/${quote.id}/outcome`, {
          outcome, lost_reason: outcome === "Rifiutato" ? lostReason : null, sync_opportunity: true,
        });
        toast.success(outcome === "Accettato" ? "Preventivo accettato" : "Preventivo rifiutato");
        onSaved?.(data);
      }
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md" data-testid="quote-actions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            {mode === "send" ? <><Send className="h-5 w-5 text-brand" /> Segna come inviato</> : <><CheckCircle2 className="h-5 w-5 text-brand" /> Registra esito</>}
          </DialogTitle>
          <DialogDescription>Preventivo {quote?.quote_number}</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="quote-action-error">{error}</div>}

        {mode === "send" ? (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Canale di invio</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger data-testid="send-channel"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{QUOTE_SENT_CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Data invio</Label><Input type="date" value={sentDate} onChange={(e) => setSentDate(e.target.value)} data-testid="send-date" /></div>
              <div className="space-y-1.5"><Label>Prossimo contatto</Label><Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="send-followup" /></div>
            </div>
            <p className="text-xs text-slate-500">Alla conferma dell'invio viene creata automaticamente una <strong>sequenza di follow-up</strong> a +3, +7, +14 e +30 giorni. Il campo "Prossimo contatto" imposta la data del primo follow-up (se lasciato vuoto, +3 giorni). I follow-up aperti si chiudono automaticamente quando il preventivo riceve un esito.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button onClick={() => setOutcome("Accettato")} data-testid="outcome-accept" className={`flex flex-1 items-center justify-center gap-2 rounded-lg border p-3 text-sm font-semibold ${outcome === "Accettato" ? "border-success bg-success/10 text-success" : "border-border text-slate-600"}`}><CheckCircle2 className="h-4 w-4" /> Accettato</button>
              <button onClick={() => setOutcome("Rifiutato")} data-testid="outcome-reject" className={`flex flex-1 items-center justify-center gap-2 rounded-lg border p-3 text-sm font-semibold ${outcome === "Rifiutato" ? "border-danger bg-danger/10 text-danger" : "border-border text-slate-600"}`}><XCircle className="h-4 w-4" /> Rifiutato</button>
            </div>
            {outcome === "Rifiutato" && (
              <div className="space-y-1.5"><Label>Motivo del rifiuto *</Label>
                <Select value={lostReason} onValueChange={setLostReason}>
                  <SelectTrigger data-testid="outcome-reason"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
                  <SelectContent className="bg-white">{QUOTE_LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {quote?.opportunity_id && (
              <p className="rounded-lg bg-brand/5 px-3 py-2 text-xs text-brand">L'opportunità collegata verrà aggiornata a {outcome === "Accettato" ? "Vinta" : "Persa"}.</p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quote-action-cancel">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="quote-action-confirm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
